import { type ReactNode, useState, useRef, useLayoutEffect } from "react";

import { SkipToContent } from "common/skip_to_content";
import { GlobalNav } from "common/proof_frame/global_nav";
import type { ProofPortal } from "constants/app_subdomains";
import { useQuery } from "util/graphql";
import { useLogout } from "common/authentication";
import { AppFrameNotaryOnboardingBanner } from "common/banners/onboarding_banner";
import ProofFrameNotaryProfileBanner from "common/banners/notary_profile_banner_proof";
import { CustomerFacingWarning } from "common/app_frame/header/dev";
import UpdateAppBanner from "common/banners/update_app_banner";
import {
  useIALaunchBanner,
  useRebrandOrgBanner,
  useProofPhase2OrgBanner,
  useProofPhase3OrgBanner,
} from "util/feature_detection";

import ProofFrameQuery, { type ProofFrame_viewer_user as User } from "./index.query.graphql";
import { OrgSideNav, SIDE_NAV_COLLAPSED_KEY } from "./org_side_nav";
import Styles from "./index.module.scss";
import { LoadingSkeleton } from "./loading_skeleton";
import IaWalkthrough from "./proof_walkthrough";
import { LaunchBanner } from "./banners/launch_banner";
import { RebrandOrgBanner } from "./banners/rebrand_org_banner";
import { Phase2RebrandOrgBanner } from "./banners/phase2_rebrand_org_banner";
import { Phase3RebrandOrgBanner } from "./banners/phase3_rebrand_org_banner";

type Props = {
  portal: ProofPortal;
  children?: ReactNode;
  user: User;
};

function useHeaderHeightCSS() {
  const ref = useRef<HTMLDivElement | null>(null);

  useLayoutEffect(() => {
    const element = ref.current;
    if (!element) {
      return;
    }
    const headerHeightObserver = new ResizeObserver(() => {
      document.documentElement.style.setProperty(
        "--proof-frame-header-height",
        `${element.offsetHeight}px`,
      );
    });
    headerHeightObserver.observe(element);
    return () => headerHeightObserver.disconnect();
  }, []);

  return ref;
}

function ProofFrameInner({ children, portal, user }: Props) {
  const iaLaunchBannerFlag = useIALaunchBanner();
  const rebrandOrgBanner = useRebrandOrgBanner();
  const phase2RebrandOrgBanner = useProofPhase2OrgBanner();
  const phase3RebrandOrgBanner = useProofPhase3OrgBanner();
  const headerRef = useHeaderHeightCSS();
  const logout = useLogout();
  const [sideNavCollapsed, setSideNavCollapsed] = useState(() => {
    const stickyValue = window.localStorage.getItem(SIDE_NAV_COLLAPSED_KEY);
    return stickyValue !== null ? JSON.parse(stickyValue) : false;
  });
  return (
    <div className={Styles.frame}>
      <SkipToContent />

      <div className={Styles.frameBody}>
        <div ref={headerRef} className={Styles.frameHeader}>
          <IaWalkthrough portal={portal} user={user} sideNavCollapsed={sideNavCollapsed} />
          <CustomerFacingWarning />
          <UpdateAppBanner />
          {iaLaunchBannerFlag && <LaunchBanner user={user} />}
          {!phase3RebrandOrgBanner && rebrandOrgBanner && (
            <RebrandOrgBanner user={user} portal={portal} />
          )}
          {!phase3RebrandOrgBanner && phase2RebrandOrgBanner && (
            <Phase2RebrandOrgBanner user={user} portal={portal} />
          )}
          {phase3RebrandOrgBanner && !window.location.hostname.endsWith("notarize.com") && (
            <Phase3RebrandOrgBanner user={user} portal={portal} />
          )}
          {user.notaryProfile && (
            <>
              <AppFrameNotaryOnboardingBanner notaryProfile={user.notaryProfile} />
              <ProofFrameNotaryProfileBanner portal={portal} user={user} />
            </>
          )}
          <GlobalNav hasSignAccess user={user} portal={portal} onLogout={logout} />
        </div>
        {portal !== "app" && user.organization?.id && (
          <div className={Styles.frameSidebar}>
            <OrgSideNav
              portal={portal}
              user={user}
              sideNavCollapsed={sideNavCollapsed}
              setSideNavCollapsed={setSideNavCollapsed}
            />
          </div>
        )}
        <main id="main-content" className={Styles.frameContent} tabIndex={-1}>
          {children}
        </main>
      </div>
    </div>
  );
}

function ProofFrame({ children, portal }: Omit<Props, "user">) {
  const { data, loading } = useQuery(ProofFrameQuery);
  if (loading) {
    return <LoadingSkeleton portal={portal} />;
  }
  const user = data?.viewer.user;
  if (user?.__typename !== "User") {
    throw new Error(`Expected User, got ${user?.__typename}`);
  }
  return (
    <ProofFrameInner user={user} portal={portal}>
      {children}
    </ProofFrameInner>
  );
}

export default ProofFrame;
